import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { EEA_COUNTRIES, REGION_OVERRIDE_KEY } from './consent.model';

export interface ConsentRegion {
  country: string | null;
  inScope: boolean;
  source: 'override' | 'geo' | 'unknown';
}

/**
 * EU/EEA gate for the consent banner. QA can force the result with
 * `?consentRegion=DE` (in scope), `?consentRegion=on`, or `?consentRegion=off`.
 * The override is kept in sessionStorage so it survives in-app navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsentRegionService {
  readonly region$ = new BehaviorSubject<ConsentRegion | null>(null);

  constructor() {
    void this.resolve();
  }

  refresh(): void {
    void this.resolve();
  }

  private async resolve(): Promise<void> {
    const override = this.readOverride();
    if (override) {
      this.region$.next(override);
      return;
    }

    try {
      const response = await fetch('https://api.country.is/');
      if (!response.ok) {
        throw new Error(`geo lookup failed (${response.status})`);
      }
      const body = await response.json();
      const country = String(body?.country || '').toUpperCase();
      this.region$.next({
        country: country || null,
        inScope: EEA_COUNTRIES.has(country),
        source: 'geo',
      });
    } catch {
      // Geo lookup unavailable/blocked: fail toward showing the banner rather than
      // silently treating an unresolved region as non-EEA (GDPR-safe default).
      this.region$.next({ country: null, inScope: true, source: 'unknown' });
    }
  }

  private readOverride(): ConsentRegion | null {
    const fromUrl = this.overrideFromLocation();
    if (fromUrl) {
      try {
        sessionStorage.setItem(REGION_OVERRIDE_KEY, fromUrl);
      } catch {
        // Private browsing can block sessionStorage. The URL param still applies this load.
      }
    }
    let stored: string | null;
    try {
      stored = sessionStorage.getItem(REGION_OVERRIDE_KEY);
    } catch {
      stored = null;
    }
    const raw = fromUrl || stored;
    if (!raw) {
      return null;
    }
    const value = raw.trim().toLowerCase();
    if (!value) {
      return null;
    }
    if (value === 'off' || value === '0' || value === 'false') {
      return { country: null, inScope: false, source: 'override' };
    }
    if (value === 'on' || value === '1' || value === 'true' || value === 'force') {
      return { country: 'DE', inScope: true, source: 'override' };
    }
    const country = value.toUpperCase();
    return {
      country,
      inScope: EEA_COUNTRIES.has(country),
      source: 'override',
    };
  }

  private overrideFromLocation(): string | null {
    const search = new URLSearchParams(window.location.search).get('consentRegion');
    if (search) {
      return search;
    }
    const hashQuery = window.location.hash.split('?')[1];
    if (!hashQuery) {
      return null;
    }
    return new URLSearchParams(hashQuery).get('consentRegion');
  }
}
